import {
  isTournamentAdmin,
  isTournamentSpectateEnabled,
  isTournamentSpectator,
  PlatformLinkingProvider,
  PlatformType,
  Region,
  TournamentAccessibility,
  TournamentBracketMatchGameEntity,
  TournamentBracketMatchGameStatus,
  TournamentBracketParticipantResultEntity,
  TournamentPlayerRole,
  TournamentPlayerStatus,
  TournamentStatus,
  TournamentTeamStatus,
  TournamentType,
} from '@okletsplay/core';
import { createSelector } from '@reduxjs/toolkit';
import { EntityStoreName } from 'app/enums/store';
import { Identifier } from 'app/interfaces';
import {
  KnockoutTournamentEntity,
  LolTournamentEntity,
  TournamentEntity,
} from 'app/interfaces/entity';
import { AppState } from 'app/interfaces/store';
import { sessionSelector } from 'app/store/session/selectors';
import config from 'config/config';
import { isNumber } from 'lodash';
import isEmpty from 'lodash/isEmpty';
import { tournamentBracketSelector } from 'modules/bracket/store/selectors';
import { LolPlatformLinkingProviderEntity } from 'modules/platform-linking/interfaces';
import {
  KNOCKOUT_TOURNAMENT_PLATFORM_TYPES,
  TOURNAMENT_DRAW_RESULTS_ENABLED_PLATFORM_TYPES,
} from 'modules/tournaments/constants';
import { TournamentLobbyInvitation } from 'modules/tournaments/interfaces';
import moment from 'moment/moment';
import { useMemo } from 'react';
import { lolProviderRegionConverter } from 'shared/utils/lol-provider-region-converter';
import { CurrentTournamentState } from './interfaces';

const FINISHED_MATCH_STATUSES = [
  TournamentBracketMatchGameStatus.Running,
  TournamentBracketMatchGameStatus.Archived,
  TournamentBracketMatchGameStatus.Completed,
];

const JOINED_PLAYER_STATUSES = [
  TournamentPlayerStatus.Ready,
  TournamentPlayerStatus.Standby,
  TournamentPlayerStatus.Played,
];

const currentTournamentState = (state: AppState): CurrentTournamentState =>
  state[EntityStoreName.CurrentTournament];

const tournamentSelector = createSelector(
  currentTournamentState,
  (currentTournament: CurrentTournamentState) =>
    currentTournament.entity as TournamentEntity | LolTournamentEntity
);

const teamsSelector = createSelector(
  currentTournamentState,
  (currentTournament: CurrentTournamentState) => currentTournament.entity?.teams ?? []
);

const teamsNamesSelector = createSelector(teamsSelector, (teams) => {
  return teams.reduce((acc: Record<string, string>, currentValue) => {
    const key = currentValue.id;

    if (!acc[key]) {
      acc[key] = '';
    }
    acc[key] = currentValue?.name ?? '';

    return acc;
  }, {});
});

const readyTeamsCountSelector = createSelector(
  teamsSelector,
  (teams) => teams?.filter((team) => team.status === TournamentTeamStatus.Ready)?.length ?? 0
);

const myTeamSelector = createSelector(
  currentTournamentState,
  sessionSelector.activeUser,
  (currentTournament, activeUser) => {
    const user = currentTournament?.entity?.teams?.find((team) =>
      team?.players?.find(
        (player) =>
          activeUser?.userId === player?.userId && JOINED_PLAYER_STATUSES.includes(player?.status)
      )
    );
    return user ?? null;
  }
);

const isMyTeamAvailableSelector = createSelector(
  tournamentSelector,
  sessionSelector.activeUser,
  (tournament, activeUser) => {
    if (!tournament) {
      return false;
    }

    const lolProvider = activeUser.connections?.find(
      (connection) => connection?.platformId === PlatformLinkingProvider?.LeagueOfLegends
    ) as LolPlatformLinkingProviderEntity;

    if (!lolProvider) {
      return false;
    }

    return (
      lolProvider.region ===
      lolProviderRegionConverter(tournament.matchSetupSettings?.region as Region)
    );
  }
);

const isTournamentTrackedSelector = createSelector(
  tournamentSelector,
  sessionSelector.activeUser,
  (tournament, activeUser) => {
    if (!tournament) {
      return false;
    }

    const trackedBy = tournament.trackedBy ?? [];
    return trackedBy.findIndex((id) => id === activeUser.id) >= 0;
  }
);

const isLolTournamentSelector = createSelector(tournamentSelector, (tournament) => {
  if (!tournament) {
    return false;
  }

  return tournament?.game?.platformType === PlatformType.LeagueOfLegends;
});

const isFinishedTournamentSelector = createSelector(tournamentSelector, (tournament) => {
  if (isEmpty(tournament)) {
    return false;
  }

  return tournament?.status === TournamentStatus.Finished;
});

const isCanceledTournamentSelector = createSelector(tournamentSelector, (tournament) => {
  if (isEmpty(tournament)) {
    return false;
  }

  return tournament?.status === TournamentStatus.Canceled;
});

const isPendingTournamentSelector = createSelector(tournamentSelector, (tournament) => {
  if (isEmpty(tournament)) {
    return false;
  }

  return tournament?.status === TournamentStatus.Pending;
});

const isTournamentEditableSelector = createSelector(tournamentSelector, (tournament) => {
  if (isEmpty(tournament)) {
    return true;
  }

  return [TournamentStatus.Pending, TournamentStatus.Checkin].includes(tournament.status);
});

const isCurrentUserCaptainSelector = createSelector(
  tournamentSelector,
  sessionSelector.activeUser,
  (tournament, activeUser) => {
    const activePlayer = tournament?.teams
      ?.flatMap((teams) => teams?.players ?? [])
      ?.find((player) => {
        return (
          player?.userId === activeUser?.userId && JOINED_PLAYER_STATUSES.includes(player?.status)
        );
      });

    return activePlayer ? activePlayer?.role === TournamentPlayerRole.Captain : true;
  }
);

const currentPlayerSelector = createSelector(
  tournamentSelector,
  sessionSelector.activeUser,
  (tournament, activeUser) => {
    const activePlayer = tournament?.teams
      ?.flatMap((teams) => teams?.players ?? [])
      ?.find((player) => {
        return (
          player?.userId === activeUser?.userId &&
          player?.status !== TournamentPlayerStatus.Declined
        );
      });

    return activePlayer;
  }
);

const joinedPlayersSelector = createSelector(tournamentSelector, (tournament) => {
  if (!tournament?.teams?.length) {
    return [];
  }

  return tournament.teams
    .flatMap((team) => team?.players ?? [])
    .filter((player) => JOINED_PLAYER_STATUSES.includes(player.status))
    .map((player) => player.user);
});

const teamsSizeSelector = createSelector(
  tournamentSelector,
  (tournament) => tournament?.teamsSettings?.teamSize ?? 0
);

const isCheckinAvailableSelector = createSelector(
  tournamentSelector,
  currentPlayerSelector,
  myTeamSelector,
  (tournament, player, team) => {
    if (
      !tournament ||
      tournament.status !== TournamentStatus.Checkin ||
      team?.status !== TournamentTeamStatus.Checkin ||
      player?.status !== TournamentPlayerStatus.Standby
    ) {
      return false;
    }

    const duration = moment.duration(moment().diff(tournament.startedAt)).asMinutes();
    return duration < config.timeToTournamentCheckIn / 60;
  }
);

const invitationsSelector = createSelector(
  tournamentSelector,
  sessionSelector.activeUser,
  (tournament, activeUser) => {
    return (tournament?.teams ?? [])
      .map((team) => {
        const isUserInvited =
          team?.players?.findIndex(
            (player) =>
              player?.userId === activeUser?.userId &&
              player?.status === TournamentPlayerStatus.Invited
          ) > -1;

        if (isUserInvited) {
          return {
            teamId: team?.id,
            teamName: team?.name,
            tournamentId: tournament?.id,
            captainName: team?.players?.find(
              (player) => player?.role === TournamentPlayerRole.Captain
            )?.user?.userName,
            startedAt: tournament?.startedAt,
          };
        }

        return null;
      })
      .filter((entity) => Boolean(entity)) as TournamentLobbyInvitation[];
  }
);

const isCurrentUserAdminSelector = createSelector(
  tournamentSelector,
  sessionSelector.activeUser,
  (tournament, activeUser) => {
    if (!activeUser || !tournament) {
      return false;
    }

    return isTournamentAdmin(activeUser, tournament);
  }
);

const isCurrentUserSpectatorSelector = createSelector(
  tournamentSelector,
  sessionSelector.activeUser,
  isCurrentUserAdminSelector,
  (tournament, activeUser, isAdmin) => {
    if (!activeUser || !tournament) return false;
    if (!isTournamentSpectateEnabled(tournament)) return false;
    if (isAdmin) return true;

    return isTournamentSpectator(activeUser, tournament);
  }
);

const isCurrentUserInvitedAsSpectatorSelector = createSelector(
  tournamentSelector,
  sessionSelector.activeUser,
  (tournament, activeUser) => {
    const currentUserId = activeUser?.id;
    if (!currentUserId || isEmpty(tournament)) {
      return false;
    }

    return Boolean(
      (<LolTournamentEntity>tournament)?.spectators?.find(
        (spectator) => spectator.user?.id === activeUser.id
      )
    );
  }
);

const isPrivateTournamentSelector = createSelector(
  tournamentSelector,
  (tournament) => tournament?.accessibility === TournamentAccessibility.Private
);

const tournamentEntryFeeSelector = createSelector(
  tournamentSelector,
  (tournament) => tournament?.prizeAndDistribution?.playerEntryFee ?? 0
);

const adminsSelector = createSelector(tournamentSelector, (tournament) => {
  if (isEmpty(tournament)) {
    return [];
  }

  return tournament.admins?.map((admin) => admin);
});

const spectatorsSelector = createSelector(tournamentSelector, (tournament) => {
  if (isEmpty(tournament)) {
    return [];
  }

  return (<LolTournamentEntity>tournament).spectators?.map((spectator) => spectator?.user) ?? [];
});

const isKnockOutTournamentSelector = createSelector(tournamentSelector, (tournament) => {
  if (!tournament?.game?.platformType) {
    return false;
  }

  return KNOCKOUT_TOURNAMENT_PLATFORM_TYPES.includes(tournament.game.platformType);
});

const isDoubleEliminationTournamentSelector = createSelector(tournamentSelector, (tournament) => {
  return (
    tournament?.matchSetupSettings?.eliminationFormat === TournamentType.DoubleEliminationTournament
  );
});

const isDrawAllowedSelector = createSelector(tournamentSelector, (tournament) => {
  if (!tournament?.game?.platformType) {
    return false;
  }

  return TOURNAMENT_DRAW_RESULTS_ENABLED_PLATFORM_TYPES.includes(tournament.game.platformType);
});

const spectatorUsersSelector = createSelector(tournamentSelector, (tournament) => {
  const spectators = (<LolTournamentEntity>tournament)?.spectators;
  if (!spectators) {
    return null;
  }

  return spectators.map((spectator) => spectator.user);
});

const lolTournamentRegionSelector = createSelector(tournamentSelector, (tournament) => {
  if (tournament?.game?.platformType === PlatformType.LeagueOfLegends) {
    return null;
  }

  return (<LolTournamentEntity>tournament)?.matchSetupSettings?.region;
});

const runningRoundsSelector = createSelector(tournamentSelector, (tournament) => {
  const runningRoundsIndex: number[] = [];
  const bracket = (tournament as LolTournamentEntity)?.bracket;
  bracket?.rounds?.forEach((round, index: number) => {
    const isFinishedMatch = round?.matches?.find(
      (match) => match?.status && FINISHED_MATCH_STATUSES.includes(match.status)
    );

    if (isFinishedMatch) {
      runningRoundsIndex.push(index);
    }
  });

  return runningRoundsIndex;
});

const teamGamesSelector = createSelector(
  tournamentSelector,
  myTeamSelector,
  (tournament, myTeam) => {
    if (!myTeam?.id) return [];

    const bracket = (tournament as LolTournamentEntity)?.bracket;
    const matches = bracket?.rounds?.flatMap((round) => round?.matches ?? []) ?? [];
    const games: TournamentBracketMatchGameEntity[] =
      matches?.flatMap((match) => match?.matchGames ?? []) ?? [];
    return games?.filter((game) => {
      const teamIds =
        game?.matchGameResults?.map(
          (result: TournamentBracketParticipantResultEntity) => result?.id
        ) ?? [];
      return teamIds?.includes(myTeam?.id);
    });
  }
);

const nextUserMatchGameSelector = createSelector(
  teamGamesSelector,
  tournamentSelector,
  (games, tournament) => {
    const bracket = (tournament as LolTournamentEntity)?.bracket;
    const matches = bracket?.rounds?.flatMap((round) => round?.matches ?? []) ?? [];
    const activeGame = games?.find(
      (game) => game?.status === TournamentBracketMatchGameStatus.Ready
    );
    const match = matches?.find((data) => data?.externalId === activeGame?.parent_id);
    const round = bracket?.rounds?.find((data) => data?.externalId === match?.round_id);
    const liveStatuses = [
      TournamentBracketMatchGameStatus.Running,
      TournamentBracketMatchGameStatus.Ready,
    ];

    if (!activeGame || !liveStatuses.includes(match?.status)) {
      return null;
    }

    return {
      ...activeGame,
      roundId: round?.externalId,
      groupId: round?.group_id,
    } as TournamentBracketMatchGameEntity & { roundId: number; groupId: number };
  }
);

const currentUserMatchGameSelector = createSelector(teamGamesSelector, (games) => {
  return games?.find((game) => game?.status === TournamentBracketMatchGameStatus.Running);
});

const isUserJoinedToTheGameSelector = createSelector(
  currentUserMatchGameSelector,
  sessionSelector.activeUser,
  (game, user) => {
    if (!game?.readyUsers?.length || !user?.id) {
      return false;
    }

    const users = game.readyUsers.map((data) => data?.user);
    return users.includes(user.id);
  }
);

const gameDurationSelector = createSelector(tournamentSelector, (tournament) => {
  if (!tournament || isEmpty(tournament) || !isNumber(tournament.matchSetupSettings.gameDuration)) {
    return 0;
  }

  return tournament.matchSetupSettings.gameDuration / 60;
});

const isUnlimitedJoinTimerSelector = createSelector(tournamentSelector, (tournament) => {
  if (!tournament || isEmpty(tournament)) {
    return true;
  }

  return tournament.matchSetupSettings?.joinGameTimer === 0;
});

const joinMatchGameTimerSelector = createSelector(tournamentSelector, (tournament) => {
  if (!tournament || isEmpty(tournament)) {
    return 0;
  }

  return (tournament?.matchSetupSettings?.joinGameTimer ?? 0) * 60;
});

const formattedSubMatchesSettingsSelector = createSelector(
  tournamentSelector,
  tournamentBracketSelector.hiddenRoundCount,
  (tournament, hiddenRoundCount) => {
    const subMatchesSettings = (tournament as KnockoutTournamentEntity)?.subMatchesSettings ?? [];
    if (!hiddenRoundCount) {
      return subMatchesSettings;
    }

    return Array.from(subMatchesSettings).slice(hiddenRoundCount);
  }
);

const breakDurationSelector = createSelector(tournamentSelector, (tournament) => {
  if (!tournament?.matchSetupSettings?.breakTime) {
    return 0;
  }

  return tournament.matchSetupSettings.breakTime;
});

const selectSpectatorFactory = (identifier?: Identifier) =>
  createSelector(tournamentSelector, (tournament) => {
    if (isEmpty(tournament) || !identifier) {
      return null;
    }

    return (<LolTournamentEntity>tournament)?.spectators?.find(
      (spectator) => spectator.user.id === identifier
    );
  });

const selectMatchGameFactory = (lobbyCode?: string) =>
  createSelector(tournamentSelector, (tournament) => {
    if (isEmpty(tournament) || !lobbyCode) {
      return null;
    }

    const bracket = (tournament as KnockoutTournamentEntity)?.bracket;
    const matches = bracket?.rounds?.flatMap((round) => round?.matches ?? []);
    const games = matches?.flatMap((match) => match?.matchGames ?? []);

    const result = games.find((game) => game.lobbyCode === lobbyCode) ?? null;
    const match = matches.find((m) => m.externalId === result?.parent_id);
    return result
      ? { ...result, roundNumber: match?.round_id ?? 0, groupNumber: match?.group_id ?? 0 }
      : null;
  });

const isSponsoredTournamentSelector = createSelector(
  tournamentSelector,
  (tournament) => tournament?.isSponsored ?? false
);

const isSinglePlayerSelector = createSelector(teamsSizeSelector, (teamsSize) => teamsSize === 1);

export const currentTournamentSelector = {
  tournament: tournamentSelector,
  region: lolTournamentRegionSelector,
  teams: teamsSelector,
  teamsSize: teamsSizeSelector,
  teamsNames: teamsNamesSelector,
  readyTeamsCount: readyTeamsCountSelector,
  isPrivateTournament: isPrivateTournamentSelector,
  invitations: invitationsSelector,
  joinedPlayers: joinedPlayersSelector,
  admins: adminsSelector,
  spectators: spectatorsSelector,
  spectatorUsers: spectatorUsersSelector,
  isMyTeamAvailable: isMyTeamAvailableSelector,
  isTournamentTracked: isTournamentTrackedSelector,
  isLolTournament: isLolTournamentSelector,
  isFinishedTournament: isFinishedTournamentSelector,
  isCanceledTournament: isCanceledTournamentSelector,
  isTournamentEditable: isTournamentEditableSelector,
  isPendingTournament: isPendingTournamentSelector,
  isCurrentUserCaptain: isCurrentUserCaptainSelector,
  isCurrentUserAdmin: isCurrentUserAdminSelector,
  isCurrentUserSpectator: isCurrentUserSpectatorSelector,
  isCurrentUserInvitedAsSpectator: isCurrentUserInvitedAsSpectatorSelector,
  isCheckinAvailable: isCheckinAvailableSelector,
  currentPlayer: currentPlayerSelector,
  myTeam: myTeamSelector,
  isKnockOutTournament: isKnockOutTournamentSelector,
  isDoubleEliminationTournament: isDoubleEliminationTournamentSelector,
  isDrawAllowed: isDrawAllowedSelector,
  runningRounds: runningRoundsSelector,
  nextUserMatchGame: nextUserMatchGameSelector,
  currentUserMatchGame: currentUserMatchGameSelector,
  isUserJoinedToTheGame: isUserJoinedToTheGameSelector,
  gameDuration: gameDurationSelector,
  isUnlimitedJoinTimer: isUnlimitedJoinTimerSelector,
  joinMatchGameTimer: joinMatchGameTimerSelector,
  breakDuration: breakDurationSelector,
  tournamentEntryFee: tournamentEntryFeeSelector,
  formattedSubMatchesSettings: formattedSubMatchesSettingsSelector,
  isSponsoredTournament: isSponsoredTournamentSelector,
  isSinglePlayer: isSinglePlayerSelector,
};

export const currentTournamentSelectorFactory = {
  useSpectator: (identifier?: Identifier) =>
    useMemo(() => selectSpectatorFactory(identifier), [identifier]),
  useMatchGame: (lobbyCode?: Identifier) =>
    useMemo(() => selectMatchGameFactory(lobbyCode), [lobbyCode]),
};
